/**
 * Safe Steps quick buy box.
 * Keeps the chosen product, option and quantity in sync with the price, the quantity-savings
 * message, the "Buy now" link (straight to checkout) and the "Add to cart" button (cart drawer).
 */
class SafeStepsBuy extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;

    this.tier2 = Number(this.dataset.tier2) || 0;
    this.tier3 = Number(this.dataset.tier3) || 0;
    this.max = Number(this.dataset.max) || 10;
    this.form = this.querySelector('[data-ss-form]');
    this.idInput = this.querySelector('[data-ss-id]');
    this.qtyInput = this.querySelector('[data-ss-qty]');
    this.panels = [...this.querySelectorAll('[data-ss-panel]')];
    this.productIndex = this.panels.length ? this.panels[0].dataset.ssPanel : null;
    this.money = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    this.addEventListener('click', (event) => this.onClick(event));
    this.qtyInput?.addEventListener('change', () => this.setQty(Number(this.qtyInput.value)));
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
    this.sync();
  }

  get panel() {
    return this.panels.find((panel) => panel.dataset.ssPanel === this.productIndex);
  }

  get quantity() {
    return Math.min(this.max, Math.max(1, Number(this.qtyInput?.value) || 1));
  }

  onClick(event) {
    const target = event.target instanceof Element ? event.target.closest('button, a') : null;
    if (!target || !this.contains(target)) return;

    if (target.hasAttribute('data-ss-product')) {
      const label = (button) => button?.querySelector('b')?.textContent?.trim();
      const previous = label(this.panel?.querySelector('[data-ss-variant].is-on'));
      this.productIndex = target.dataset.ssProduct;
      this.mark(target);
      this.panels.forEach((panel) => (panel.hidden = panel.dataset.ssPanel !== this.productIndex));
      // Carry the chosen size over to the newly selected pack when it exists there.
      const options = [...(this.panel?.querySelectorAll('[data-ss-variant]') || [])];
      if (previous && !options.some((option) => option.classList.contains('is-on'))) {
        const match = options.find((option) => !option.disabled && label(option) === previous);
        if (match) this.mark(match);
      }
      this.sync();
    } else if (target.hasAttribute('data-ss-variant')) {
      this.mark(target);
      this.sync();
    } else if (target.hasAttribute('data-ss-minus')) {
      this.setQty(this.quantity - 1);
    } else if (target.hasAttribute('data-ss-plus')) {
      this.setQty(this.quantity + 1);
    } else if (target.hasAttribute('data-ss-buy') && target.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      this.panel?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  mark(button) {
    button.parentElement?.querySelectorAll('.ss-opt').forEach((option) => {
      const on = option === button;
      option.classList.toggle('is-on', on);
      option.setAttribute('aria-checked', String(on));
    });
  }

  setQty(value) {
    if (!this.qtyInput) return;
    this.qtyInput.value = String(Math.min(this.max, Math.max(1, Math.round(value) || 1)));
    this.sync();
  }

  /** The selected variant for the visible product, or null when a choice is still needed. */
  selection() {
    const panel = this.panel;
    if (!panel) return null;
    if (panel.dataset.default) return { id: panel.dataset.default, price: Number(panel.dataset.price) };
    const chosen = panel.querySelector('[data-ss-variant].is-on');
    return chosen ? { id: chosen.dataset.ssVariant, price: Number(chosen.dataset.price) } : null;
  }

  discount(quantity) {
    if (quantity >= 3) return this.tier3;
    if (quantity === 2) return this.tier2;
    return 0;
  }

  sync() {
    const panel = this.panel;
    const choice = this.selection();
    const quantity = this.quantity;
    const percent = this.discount(quantity);
    const unit = choice ? choice.price : Number(panel?.dataset.price || 0);
    const original = unit * quantity;
    const total = original - Math.round((original * percent) / 100);

    const set = (selector, html) => {
      const element = this.querySelector(selector);
      if (element) element.innerHTML = html;
    };

    set('[data-ss-unit]', this.money.format(unit / 100));
    if (this.idInput) this.idInput.value = choice ? choice.id : '';

    const minus = this.querySelector('[data-ss-minus]');
    const plus = this.querySelector('[data-ss-plus]');
    if (minus) minus.disabled = quantity <= 1;
    if (plus) plus.disabled = quantity >= this.max;

    if (this.tier2 > 0 || this.tier3 > 0) {
      let note = `Buy 2 and save ${this.tier2}%. Buy 3 or more and save ${this.tier3}%.`;
      if (percent > 0 && quantity === 2 && this.tier3 > this.tier2) {
        note = `<b>${percent}% quantity discount applied.</b> Add 1 more to save ${this.tier3}%.`;
      } else if (percent > 0) {
        note = `<b>${percent}% quantity discount applied.</b>`;
      }
      set('[data-ss-note]', note);
    }

    const buy = this.querySelector('[data-ss-buy]');
    const add = this.querySelector('[data-ss-add]');
    if (buy) {
      if (choice) {
        buy.setAttribute('href', `${this.dataset.cartUrl}/${choice.id}:${quantity}`);
        buy.removeAttribute('aria-disabled');
        set('[data-ss-buy-text]', this.dataset.buyLabel || 'Buy now');
        set(
          '[data-ss-total]',
          percent > 0
            ? `<s>${this.money.format(original / 100)}</s> ${this.money.format(total / 100)}`
            : this.money.format(total / 100)
        );
      } else {
        buy.setAttribute('href', '#');
        buy.setAttribute('aria-disabled', 'true');
        set('[data-ss-buy-text]', this.dataset.pickLabel || 'Select an option to buy');
        set('[data-ss-total]', '');
      }
    }
    if (add) add.disabled = !choice;

    const details = this.querySelector('[data-ss-details]');
    if (details && panel?.dataset.url) details.setAttribute('href', panel.dataset.url);
  }

  async onSubmit(event) {
    const choice = this.selection();
    if (!choice) {
      event.preventDefault();
      return;
    }
    event.preventDefault();

    const add = this.querySelector('[data-ss-add]');
    const quantity = this.quantity;
    const label = add ? add.textContent : '';
    if (add) {
      add.disabled = true;
      add.textContent = 'Adding…';
    }

    try {
      const response = await fetch(`${this.dataset.addUrl}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(choice.id), quantity }] }),
      });
      if (!response.ok) throw new Error('Add to cart failed');
      await this.announce(choice.id, quantity);
    } catch (error) {
      // Fall back to the standard form post so the shopper is never stuck.
      this.form?.submit();
      return;
    } finally {
      if (add) {
        add.disabled = false;
        add.textContent = label;
      }
    }
  }

  /** Tells the theme's cart drawer and cart icon that the cart changed, then opens the drawer. */
  async announce(id, quantity) {
    try {
      const [{ CartLinesUpdateEvent }, cart] = await Promise.all([
        import('@shopify/events'),
        fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json()),
      ]);
      const promise = Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: quantity, source: 'ss-buy', didError: false },
      });
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(id), quantity }],
          promise,
        })
      );
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        if (!drawer.isOpen) drawer.open();
      } else {
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      window.location.href = this.dataset.cartUrl;
    }
  }
}

if (!customElements.get('ss-buy')) customElements.define('ss-buy', SafeStepsBuy);
